import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { AdminDemoStatus, AdminDemoResetResult } from '@repo/shared-types';
import { adminFetchApi } from '../lib/api';

/**
 * THI-257 — `enabled`/`slugs` for the "Reset dữ liệu mẫu" button. Disabled
 * on prod (`DEMO_RESET_ALLOWED` unset there), so most orgs never render this
 * query's result as anything but "hide the button" — see §4.3-style caller
 * discipline: `OrgDetailPage` only renders the button when BOTH this says
 * enabled AND the org's own slug is in `slugs`.
 */
export function useAdminDemoStatus() {
  return useQuery({
    queryKey: ['admin', 'demo', 'status'],
    queryFn: () => adminFetchApi<AdminDemoStatus>('/admin/demo'),
    staleTime: 60_000,
  });
}

/** `orgName` is only for the success toast ("Đã reset {name}") — the request
 *  itself is keyed by slug. `['admin','orgs']` invalidation also covers
 *  `['admin','orgs', orgId]` (TanStack's prefix match), so the org detail
 *  page refetches too. */
export function useResetDemoOrg(slug: string, orgName: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => adminFetchApi<AdminDemoResetResult>('/admin/demo/reset', { method: 'POST', body: JSON.stringify({ slug }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'orgs'] });
      toast.success(`Đã reset ${orgName}`);
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
